import { z } from 'zod';
import { isoTimestamp } from './common';
import { DEFAULT_FILE_MAX_BYTES, fileNameSchema } from './files';
import { resourceNameSchema } from './tools';

// --- Constants ---

/** Embedding sizes a workspace may use; each has its own vector column and HNSW index. */
export const WORKSPACE_EMBEDDING_DIMS = [768, 1024, 1536] as const;
export type WorkspaceEmbeddingDims = (typeof WORKSPACE_EMBEDDING_DIMS)[number];

/** Provider types that can embed text. */
export const WORKSPACE_EMBEDDING_PROVIDER_TYPES = [
  'openai',
  'google',
  'openai_compatible',
] as const;

export const WORKSPACE_DOCUMENT_STATUSES = ['pending', 'indexing', 'ready', 'failed'] as const;
export type WorkspaceDocumentStatus = (typeof WORKSPACE_DOCUMENT_STATUSES)[number];

export const WORKSPACE_DOCUMENT_MEDIA_TYPES = [
  'text/plain',
  'text/markdown',
  'text/csv',
  'application/json',
  'text/html',
  'application/pdf',
] as const;
export type WorkspaceDocumentMediaType = (typeof WORKSPACE_DOCUMENT_MEDIA_TYPES)[number];

export const WORKSPACE_CHUNK_TOKENS = 800;
export const WORKSPACE_CHUNK_OVERLAP_TOKENS = 100;
/** Rough characters-per-token used to size chunks without a tokenizer. */
export const CHARS_PER_TOKEN = 4;
export const WORKSPACE_SEARCH_MAX_LIMIT = 10;
export const WORKSPACE_SEARCH_DEFAULT_LIMIT = 5;
/** Characters returned per `read_workspace_document` page. */
export const WORKSPACE_READ_PAGE = 40_000;
/** Reciprocal-rank-fusion constant for hybrid search. */
export const WORKSPACE_RRF_K = 60;

// --- Workspaces ---

const descriptionSchema = z.string().trim().max(2000);

export const workspaceEmbeddingSchema = z.object({
  providerConnectionId: z.uuid(),
  model: z.string().trim().min(1).max(200),
  dims: z.union(WORKSPACE_EMBEDDING_DIMS.map((d) => z.literal(d))),
});
export type WorkspaceEmbedding = z.infer<typeof workspaceEmbeddingSchema>;

export const createWorkspaceRequestSchema = z.object({
  name: resourceNameSchema,
  description: descriptionSchema.default(''),
  /** Null keeps the workspace keyword-only. */
  embedding: workspaceEmbeddingSchema.nullable().default(null),
});
export type CreateWorkspaceRequest = z.infer<typeof createWorkspaceRequestSchema>;

export const updateWorkspaceRequestSchema = z
  .object({ description: descriptionSchema.optional() })
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export type UpdateWorkspaceRequest = z.infer<typeof updateWorkspaceRequestSchema>;

/** Omitting `embedding` keeps the current settings; null switches to keyword-only. */
export const reindexWorkspaceRequestSchema = z.object({
  embedding: workspaceEmbeddingSchema.nullable().optional(),
});
export type ReindexWorkspaceRequest = z.infer<typeof reindexWorkspaceRequestSchema>;

export const workspaceDtoSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string(),
  embedding: workspaceEmbeddingSchema.nullable(),
  documentCount: z.number().int(),
  readyCount: z.number().int(),
  totalBytes: z.number().int(),
  /** Embedding settings change only through a reindex once chunks exist. */
  embeddingLocked: z.boolean(),
  createdAt: isoTimestamp,
  updatedAt: isoTimestamp,
});
export type WorkspaceDto = z.infer<typeof workspaceDtoSchema>;

// --- Documents ---

export const workspaceDocumentDtoSchema = z.object({
  id: z.uuid(),
  workspaceId: z.uuid(),
  name: z.string(),
  mediaType: z.string(),
  sizeBytes: z.number().int(),
  sha256: z.string(),
  status: z.enum(WORKSPACE_DOCUMENT_STATUSES),
  error: z.string().nullable(),
  chunkCount: z.number().int(),
  createdAt: isoTimestamp,
  updatedAt: isoTimestamp,
});
export type WorkspaceDocumentDto = z.infer<typeof workspaceDocumentDtoSchema>;

export const createTextDocumentRequestSchema = z.object({
  name: fileNameSchema,
  text: z.string().min(1).max(DEFAULT_FILE_MAX_BYTES),
});
export type CreateTextDocumentRequest = z.infer<typeof createTextDocumentRequestSchema>;

// --- Search ---

export const workspaceSearchRequestSchema = z.object({
  query: z.string().trim().min(1).max(1000),
  limit: z
    .number()
    .int()
    .min(1)
    .max(WORKSPACE_SEARCH_MAX_LIMIT)
    .default(WORKSPACE_SEARCH_DEFAULT_LIMIT),
});
export type WorkspaceSearchRequest = z.infer<typeof workspaceSearchRequestSchema>;

export const workspaceSearchHitSchema = z.object({
  documentId: z.uuid(),
  documentName: z.string(),
  chunkId: z.uuid(),
  ordinal: z.number().int(),
  score: z.number(),
  text: z.string(),
});
export type WorkspaceSearchHit = z.infer<typeof workspaceSearchHitSchema>;

export const workspaceSearchResultSchema = z.object({
  mode: z.enum(['keyword', 'vector', 'hybrid']),
  hits: z.array(workspaceSearchHitSchema),
  notes: z.array(z.string()),
});
export type WorkspaceSearchResult = z.infer<typeof workspaceSearchResultSchema>;
